import { Divider } from "@shared/components/Layout/Divider";
import { formatNumber } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { usePortfolioValues } from "@/hooks/portfolio/usePortfolioValues";
import { useCountUp } from "@/hooks/useCountUp";

const ZERO_BD = new BigDecimal(0);
const FORMAT_USD = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
} as const;

export function shouldShowLegacyStakedBalance(
    susdvBalance: BigDecimal | null
): boolean {
    return susdvBalance?.gt(0) ?? false;
}

/**
 * @version 1.0.0
 * @published 2026-04-08
 * @history
 *   1.0.0 — Initial publish from prototype/pages/USDv/USDvPositionsPage.tsx.
 *           Portfolio card shown on the logged-in USDv page. Animated border
 *           draws on mount, count-up on the earnings value, and a two-row
 *           balance breakdown below.
 */
export const PortfolioCard = React.memo(
    function PortfolioCard(): React.ReactElement {
        const {
            usdvBalance,
            susdvBalance,
            stakedUsdvValue,
            totalYield,
            isLoaded
        } = usePortfolioValues();
        const hasLegacyStakedBalance =
            shouldShowLegacyStakedBalance(susdvBalance);
        const legacyEarningsDisplay = useCountUp(
            totalYield.toNumber(),
            isLoaded && hasLegacyStakedBalance,
            600
        );

        return (
            <div className="relative border-2 border-transparent p-5 md:p-8 flex flex-col gap-6">
                {/* Top — ember, center out */}
                <div
                    className="absolute -inset-px pointer-events-none"
                    style={{
                        borderTop: "2px solid rgb(var(--color-accent-base))",
                        animation: "stake-top-in 200ms ease-out 0ms both"
                    }}
                />
                {/* Left — top to bottom */}
                <div
                    className="absolute -inset-px pointer-events-none"
                    style={{
                        borderLeft: "2px solid rgb(var(--color-line-base))",
                        animation: "stake-sides-in 200ms ease-out 200ms both"
                    }}
                />
                {/* Right — top to bottom */}
                <div
                    className="absolute -inset-px pointer-events-none"
                    style={{
                        borderRight: "2px solid rgb(var(--color-line-base))",
                        animation: "stake-sides-in 200ms ease-out 200ms both"
                    }}
                />
                {/* Bottom — outside in */}
                <div
                    className="absolute -inset-px pointer-events-none"
                    style={{
                        borderBottom: "2px solid rgb(var(--color-line-base))",
                        animation: "stake-bottom-in 200ms ease-out 400ms both"
                    }}
                />
                {/* Earnings */}
                <div className="flex flex-col gap-3">
                    {hasLegacyStakedBalance ? (
                        <>
                            <Text
                                variant="body"
                                render={<span />}
                                className="text-foreground-neutral"
                            >
                                Legacy sUSDv earnings
                            </Text>
                            <Text
                                variant="number-lg"
                                className="text-foreground-base"
                            >
                                ${legacyEarningsDisplay}
                            </Text>
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-neutral"
                            >
                                Upgrade this balance from Account.
                            </Text>
                        </>
                    ) : (
                        <Text
                            variant="body"
                            render={<span />}
                            className="text-foreground-neutral"
                        >
                            Explore available earning positions.
                        </Text>
                    )}
                </div>

                <Divider />

                {/* Stats rows */}
                <div className="flex flex-col gap-4">
                    {[
                        {
                            label: "USDv Balance",
                            value: `$${formatNumber(usdvBalance ?? ZERO_BD, FORMAT_USD)}`
                        },
                        ...(hasLegacyStakedBalance
                            ? [
                                  {
                                      label: "Legacy sUSDv Balance",
                                      value: `$${formatNumber(stakedUsdvValue ?? ZERO_BD, FORMAT_USD)}`
                                  }
                              ]
                            : [])
                    ].map(({ label, value }) => (
                        <div
                            key={label}
                            className="flex items-center justify-between leading-none"
                        >
                            <Text
                                variant="body"
                                render={<span />}
                                className="text-foreground-neutral"
                            >
                                {label}
                            </Text>
                            <Text
                                variant="body"
                                render={<span />}
                                className="text-foreground-base tabular-nums"
                            >
                                {value}
                            </Text>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
);
